import type { CatalogResultCard } from './types'

/**
 * Carries a category-page facet selection (`CatalogSeriesPage`'s
 * `Shaft Style: Hollow` checkbox group, `specs[<name>]=<value>` on the wire)
 * into `CatalogSeriesConfigurePage`'s already-fetched `allRows` — a pure,
 * client-side narrowing of rows already on hand, no second fetch.
 *
 * Deliberately a SEPARATE module from `catalog-series-filters.ts`. That
 * module's `filterSeriesRows`/`SeriesFilterSelection` reads
 * `card.configSchema.segments` — numeric dimensions (Length L, min/max
 * ranges) unioned across a series' rows. This module reads
 * `card.specs` — the normalized `{name, value}` string pairs the category
 * facet sidebar reads (ADR-0120 D7.1, `CatalogResultCard.specs`, a verbatim
 * per-item passthrough). The two axes don't translate onto each other (a
 * numeric dimension segment has no normalized spec name/value form, and a
 * spec pair has no min/max), so this stays its own predicate rather than
 * folding into `filterSeriesRows` or `SeriesFilterSelection` — see the
 * architect's finding this was scoped against.
 */

/**
 * One row's outcome against a carried-over selection:
 * - `'match'` — the row's `specs` contains every selected `name`/`value` pair.
 * - `'no-match'` — the row DOES carry `specs`, but not all of the selected
 *   pairs are in it.
 * - `'omitted'` — the row carries NO `specs` field at all (omitted on the
 *   wire, per ADR-0090/0091 "omit, don't fabricate" — absence is not the same
 *   claim as "carries no matching spec"). Never silently folded into
 *   `'no-match'`: a row with unmeasured specs isn't known to disagree with
 *   the filter, it's simply not know-able either way, and the UI discloses
 *   that count separately rather than hiding it inside a smaller-than-
 *   expected filtered list.
 */
export type CarriedFacetRowStatus = 'match' | 'no-match' | 'omitted'

export function classifyRowAgainstCarriedFacets(
  row: CatalogResultCard,
  selection: Readonly<Record<string, string>>,
): CarriedFacetRowStatus {
  if (Object.keys(selection).length === 0) return 'match'
  if (row.specs === undefined) return 'omitted'
  const specs = row.specs
  const matchesEvery = Object.entries(selection).every(([name, value]) =>
    specs.some((spec) => spec.name === name && spec.value === value),
  )
  return matchesEvery ? 'match' : 'no-match'
}

export interface CarriedFacetFilterResult {
  /** Rows that matched every selected spec pair — what the table actually
   * renders. Rows with no `specs` on file are NOT included here (see
   * `omittedCount`'s doc comment for why that's not the same as excluding
   * them as non-matches). */
  readonly visibleRows: readonly CatalogResultCard[]
  /** Count of rows carrying no `specs` field at all — disclosed separately
   * in the UI (e.g. "M items have no spec data on file") rather than
   * silently dropped with no explanation for why the count looks smaller
   * than the series' full row count. */
  readonly omittedCount: number
  /** `visibleRows.length` — named for readability at call sites that also
   * read `totalCount`. */
  readonly matchCount: number
  /** `rows.length` as passed in — the series' full, unfiltered row count,
   * for the "all N items match" degenerate-case message and the
   * "showing X of Y" disclosure. */
  readonly totalCount: number
}

/**
 * Narrows `rows` (a series' already-fetched `allRows`) by a carried-over
 * category-facet `selection`. An empty `selection` is a no-op: every row is
 * `visibleRows`, `omittedCount` is `0`.
 */
export function filterRowsByCarriedFacets(
  rows: readonly CatalogResultCard[],
  selection: Readonly<Record<string, string>>,
): CarriedFacetFilterResult {
  const visibleRows: CatalogResultCard[] = []
  let omittedCount = 0

  for (const row of rows) {
    const status = classifyRowAgainstCarriedFacets(row, selection)
    if (status === 'match') {
      visibleRows.push(row)
    } else if (status === 'omitted') {
      omittedCount += 1
    }
  }

  return { visibleRows, omittedCount, matchCount: visibleRows.length, totalCount: rows.length }
}

/**
 * Renders a carried selection as the banner's "Shaft Style = Hollow" text —
 * joined with a separator when more than one spec was carried over. Reuses
 * the same `name`/`value` pairs the URL round-trips (see
 * `catalog-series-facet-filter.ts`'s `parseFacetSelection`).
 */
export function describeCarriedFacetSelection(selection: Readonly<Record<string, string>>): string {
  return Object.entries(selection)
    .map(([name, value]) => `${name} = ${value}`)
    .join(', ')
}
